import { CalendarClock, Phone, Mail, Clock, Siren } from "lucide-react";
import { PageHero, Reveal, GoldButton, GhostButton, usePageMeta } from "@/components/kit";
import { HERO_BG, PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO, HOURS_WEEK, HOURS_TIME, AFTER_HOURS } from "@/lib/site";

const SCHED_URL = process.env.REACT_APP_SCHEDULING_URL;

export default function SchedulePage() {
  usePageMeta(
    "Schedule an Appointment | Redeemer Solutions LLC",
    "Book a free on-site assessment with Redeemer Solutions LLC. Open Monday–Friday 7 AM–4:30 PM, with after-hours emergency service across Middle Tennessee."
  );
  return (
    <div data-testid="schedule-page">
      <PageHero
        testid="schedule-hero"
        eyebrow="Schedule Appointment"
        lines={["Pick a Time.", { t: "We'll Be There.", gold: true }]}
        sub="Book a service visit. We'll diagnose the issue, explain your options in plain language, and leave you with an honest quote."
        image={HERO_BG}
        imageAlt="Schedule an appointment with Redeemer Solutions LLC"
      />

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        {SCHED_URL ? (
          <Reveal>
            <div className="bg-[#121216] border border-gold/15 rounded-sm overflow-hidden">
              <iframe
                src={SCHED_URL}
                data-testid="schedule-embed-frame"
                title="Schedule an appointment with Redeemer Solutions LLC"
                className="w-full h-[720px] bg-white"
              />
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <div data-testid="schedule-fallback" className="bg-[#121216] border border-gold/20 rounded-sm p-10 sm:p-14 text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center border border-gold/40 bg-gold/5 rounded-full">
                <CalendarClock size={28} className="text-gold" />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#F4EFE6] mt-7">
                Our Online Scheduler Is Being Connected
              </h2>
              <p className="text-zinc-400 text-base leading-relaxed mt-4 max-w-xl mx-auto">
                Booking an appointment is still fast — call or email us and we'll lock in a time that works for you,
                usually within the same week.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-9">
                <GoldButton href={PHONE_TEL} testid="schedule-call-button">
                  <Phone size={16} /> Call {PHONE}
                </GoldButton>
                <GhostButton href={EMAIL_MAILTO} testid="schedule-email-button">
                  <Mail size={16} /> Email Us
                </GhostButton>
                <GhostButton to="/request-quote" testid="schedule-quote-button">
                  Request a Quote Instead
                </GhostButton>
              </div>
              <div className="gold-hairline my-10" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-lg mx-auto text-left">
                <div className="flex items-start gap-3">
                  <Clock size={16} className="text-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[#F4EFE6]">{HOURS_WEEK}</p>
                    <p className="text-sm text-zinc-400">{HOURS_TIME}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Siren size={16} className="text-red-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[#F4EFE6]">After Hours & Weekends</p>
                    <p className="text-sm text-zinc-400">{AFTER_HOURS}</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        )}
      </section>
    </div>
  );
}
